import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const STATUS_OPTIONS = ['Draft', 'Pending', 'PendingApproval', 'Approved', 'Booked', 'Paid', 'Rejected'];

const LeaveSettlementsPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>();
  const { empLabel } = useEmployeeOptions({ employment_status: null });

  const listParams = useMemo(
    () => ({ status: statusFilter || undefined }),
    [statusFilter]
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.leaveSettlements(listParams),
    queryFn: () => hrmsService.listLeaveSettlements(listParams),
  });

  const stats = useMemo(() => {
    const pending = rows.filter((r: any) =>
      ['Draft', 'Pending', 'PendingApproval'].includes(String(r.status))
    ).length;
    const net = rows.reduce((s: number, r: any) => s + Number(r.net_amount || 0), 0);
    return { total: rows.length, pending, net };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Settlement #',
      dataIndex: 'settlement_number',
      width: 140,
      fixed: 'left',
      render: (v: string, r) => (
        <a onClick={() => navigate(`/hr/leave-settlements/${r.id}`)}>{v || 'Open'}</a>
      ),
    },
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      ellipsis: true,
      render: (id: string) => empLabel(id),
    },
    { title: 'Leave type', dataIndex: 'leave_type', width: 110 },
    { title: 'Days', dataIndex: 'eligible_days', width: 80 },
    { title: 'Mode', dataIndex: 'settlement_mode', width: 120, render: (v: string) => v || '—' },
    { title: 'Net amount', dataIndex: 'net_amount', width: 120 },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/hr/leave-settlements/${r.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Leave settlements"
          subtitle="UAE/MENA leave pay — payroll addition or salary advance"
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic title="Total" value={stats.total} />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic title="Draft / Pending" value={stats.pending} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Net amount" value={stats.net} precision={2} />
          </Card>
        </Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 180 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS.map((v) => ({ value: v, label: v }))}
          />
          <Text type="secondary">{rows.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({
          onClick: () => navigate(`/hr/leave-settlements/${r.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </WorkspaceLayout>
  );
};

export default LeaveSettlementsPage;
